import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Navigate } from "react-router-dom";
import apiService from "../services/api";
import EmployeeApprovalModal from "../components/EmployeeApprovalModal";
import UploadResultsModal from "../components/UploadResultsModal";
import EmployeeReferenceModal from "../components/EmployeeReferenceModal";
import "../styles/Upload.css";

const Upload = () => {
  const [file, setFile] = useState(null);
  const [dataType, setDataType] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showReferenceModal, setShowReferenceModal] = useState(false);
  const [newEmployees, setNewEmployees] = useState([]);
  const [uploadResult, setUploadResult] = useState(null);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [clearMonth, setClearMonth] = useState("");
  const [clearDataType, setClearDataType] = useState("companies");
  const [clearing, setClearing] = useState(false);
  const { user, hasPermission } = useAuth();

  useEffect(() => {
    loadAvailableMonths();
  }, []);

  const loadAvailableMonths = async () => {
    try {
      const [dashboardResponse, presenceResponse] = await Promise.all([
        apiService
          .getAvailableMonths()
          .catch(() => ({ success: false, data: [] })),
        apiService
          .getPresenceAvailableMonths()
          .catch(() => ({ success: false, months: [] })),
      ]);
      const dashboardMonths = dashboardResponse.data || [];
      const presenceMonths = presenceResponse.months || [];
      const allMonths = [
        ...new Set([...dashboardMonths, ...presenceMonths]),
      ].sort((a, b) => b.localeCompare(a));
      setAvailableMonths(allMonths);
      if (allMonths.length > 0) {
        setClearMonth(allMonths[0]);
      }
    } catch (error) {
      console.error("Failed to load available months:", error);
    }
  };

  const handleClearData = async () => {
    if (!clearMonth) {
      setError("אנא בחר חודש למחיקה");
      return;
    }

    const confirmMsg = `האם אתה בטוח שברצונך למחוק את כל הנתונים של חודש ${clearMonth}?\n\nפעולה זו לא ניתנת לביטול!`;
    if (!window.confirm(confirmMsg)) {
      return;
    }

    setClearing(true);
    setError("");
    setSuccess("");

    try {
      const result = await apiService.clearUploadedData(
        clearMonth,
        clearDataType,
      );
      if (result.success) {
        setSuccess(
          `✅ נמחקו בהצלחה: ${result.deleted_hour_globals || 0} רשומות שעות, ${result.deleted_presence || 0} רשומות נוכחות`,
        );
        loadAvailableMonths();
      } else {
        setError(result.error || "שגיאה במחיקת נתונים");
      }
    } catch (err) {
      console.error("Clear data error:", err);
      setError(err.response?.data?.error || "שגיאה במחיקת נתונים");
    } finally {
      setClearing(false);
    }
  };

  const formatMonth = (monthStr) => {
    if (!monthStr) return "";
    const [year, month] = monthStr.split("-");
    const monthNames = [
      "ינואר",
      "פברואר",
      "מרץ",
      "אפריל",
      "מאי",
      "יוני",
      "יולי",
      "אוגוסט",
      "ספטמבר",
      "אוקטובר",
      "נובמבר",
      "דצמבר",
    ];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  // Restrict to admins only
  if (!hasPermission("admin")) {
    return <Navigate to='/' replace />;
  }

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Validate file type
      const validTypes = [".csv", ".xls", ".xlsx"];
      const fileExtension = selectedFile.name
        .substring(selectedFile.name.lastIndexOf("."))
        .toLowerCase();

      if (validTypes.includes(fileExtension)) {
        setFile(selectedFile);
        setError("");
      } else {
        setFile(null);
        setError("אנא בחר קובץ תקין (.csv, .xls, .xlsx)");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setUploadProgress(0);

    if (!file) {
      setError("אנא בחר קובץ");
      return;
    }

    if (!dataType) {
      setError("אנא בחר סוג נתונים");
      return;
    }

    setLoading(true);

    try {
      const result = await apiService.uploadFile(file, dataType, (progress) => {
        setUploadProgress(progress);
      });

      if (result.success) {
        setUploadResult(result);

        // Check for employees with reference data from other partnerships
        const employeesWithReference =
          result.new_employees?.filter(
            (emp) => emp.reference_data && emp.copied_from_other_partnership,
          ) || [];

        if (employeesWithReference.length > 0) {
          // Show reference modal for employees copied from other partnerships
          setNewEmployees(result.new_employees);
          setShowReferenceModal(true);
        } else if (dataType === "tm" && result.summary) {
          // For T&M files, show results modal with summary
          setShowResultsModal(true);
        } else {
          // Build success message for non-T&M files
          let successMsg = `✅ הועלו בהצלחה ${result.rows_imported || 0} שורות`;
          if (result.existing_employees_count) {
            successMsg += ` | ${result.existing_employees_count} עובדים קיימים עודכנו`;
          }
          if (result.new_employees && result.new_employees.length > 0) {
            successMsg += ` | ${result.new_employees.length} עובדים חדשים נוספו`;
          }
          setSuccess(successMsg);
        }

        setFile(null);
        setDataType("");
        setUploadProgress(0);
        // Reset file input
        document.getElementById("file-input").value = "";
      } else {
        setError(result.error || "ההעלאה נכשלה");
      }
    } catch (err) {
      console.error("Upload error:", err);
      setError(err.response?.data?.error || "אירעה שגיאה בלתי צפויה");
    } finally {
      setLoading(false);
    }
  };

  const handleResultsClose = () => {
    setShowResultsModal(false);
    setSuccess("✅ העלאה הושלמה בהצלחה!");
  };

  const handleApprovalModalClose = () => {
    setShowApprovalModal(false);
    setNewEmployees([]);
  };

  const handleEmployeeApproved = () => {
    // Refresh or update UI as needed
    console.log("Employee approved");
  };

  const handleReferenceModalClose = () => {
    setShowReferenceModal(false);
    setNewEmployees([]);
    setSuccess("✅ העלאה הושלמה! עובדים מחברות אחרות נשמרו.");
  };

  const handleReferenceApproved = () => {
    setShowReferenceModal(false);
    setNewEmployees([]);
    setSuccess("✅ העובדים נשמרו בהצלחה!");
  };

  return (
    <div className='upload-page'>
      {loading && (
        <div className='loading-overlay'>
          <div className='loading-content'>
            <div className='loading-spinner'></div>
            <h2>מעלה קובץ...</h2>
            <p>אנא המתן, הקובץ בתהליך העלאה</p>
            {uploadProgress > 0 && (
              <div className='loading-progress'>
                <div className='loading-progress-bar'>
                  <div
                    className='loading-progress-fill'
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <span className='loading-progress-text'>{uploadProgress}%</span>
              </div>
            )}
          </div>
        </div>
      )}
      <div className='upload-container'>
        <div className='upload-header'>
          <div>
            <h1>📊 העלאת קובץ נתונים</h1>
            <p className='subtitle'>
              בחר קובץ Excel או CSV וציין את סוג הנתונים
            </p>
          </div>
        </div>

        <div className='user-info-card'>
          <div className='info-row'>
            <span className='info-label'>👤 משתמש:</span>
            <span className='info-value'>{user?.username || "לא ידוע"}</span>
          </div>
          <div className='info-row'>
            <span className='info-label'>🔑 הרשאה:</span>
            <span className='info-value'>{user?.permission || "לא ידוע"}</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className='upload-form'>
          <div className='form-field'>
            <label htmlFor='file-input'>
              בחר קובץ <span className='required'>*</span>
            </label>
            <input
              type='file'
              id='file-input'
              accept='.csv,.xls,.xlsx'
              onChange={handleFileChange}
              required
            />
            {file && (
              <div className='file-info'>
                📄 נבחר: <strong>{file.name}</strong> (
                {(file.size / 1024).toFixed(2)} KB)
              </div>
            )}
          </div>

          <div className='form-field'>
            <label>
              בחר סוג נתונים <span className='required'>*</span>
            </label>
            <div className='radio-group'>
              <div className='radio-option'>
                <input
                  type='radio'
                  id='option-tm'
                  name='data-type'
                  value='tm'
                  checked={dataType === "tm"}
                  onChange={(e) => setDataType(e.target.value)}
                  required
                />
                <label htmlFor='option-tm'>T&M</label>
              </div>
              <div className='radio-option'>
                <input
                  type='radio'
                  id='option-companies'
                  name='data-type'
                  value='companies'
                  checked={dataType === "companies"}
                  onChange={(e) => setDataType(e.target.value)}
                  required
                />
                <label htmlFor='option-companies'>חברות</label>
              </div>
            </div>
          </div>

          {loading && uploadProgress > 0 && (
            <div className='progress-bar'>
              <div
                className='progress-fill'
                style={{ width: `${uploadProgress}%` }}
              >
                {uploadProgress}%
              </div>
            </div>
          )}

          {error && (
            <div className='status-message error-message'>❌ {error}</div>
          )}

          {success && (
            <div className='status-message success-message'>{success}</div>
          )}

          <button
            type='submit'
            className='upload-button'
            disabled={loading || !file || !dataType}
          >
            {loading ? "מעלה..." : "העלה קובץ"}
          </button>
        </form>

        <div className='upload-info'>
          <h3>📋 הוראות</h3>
          <ul>
            <li>
              <strong>זמן וחומרים:</strong> נתוני זמן וחומרים - שעות עובדים
              ופרטי עבודה
            </li>
            <li>
              <strong>חברות:</strong> נתוני חברות - כל גיליון מייצג לקוח
            </li>
            <li>
              פורמטים נתמכים: <code>.csv</code>, <code>.xls</code>,{" "}
              <code>.xlsx</code>
            </li>
            <li>גודל קובץ מקסימלי: 50 MB</li>
          </ul>
        </div>

        {/* Clear Data Section */}
        <div className='clear-data-section'>
          <h3>🗑️ מחיקת נתונים שהועלו</h3>
          <p className='clear-data-warning'>
            ⚠️ פעולה זו תמחק את כל הנתונים שהועלו עבור החודש הנבחר. פעולה זו לא
            ניתנת לביטול!
          </p>
          <div className='clear-data-form'>
            <div className='form-field'>
              <label>בחר חודש למחיקה:</label>
              <select
                value={clearMonth}
                onChange={(e) => setClearMonth(e.target.value)}
                className='clear-month-select'
              >
                <option value=''>בחר חודש</option>
                {availableMonths.map((month) => (
                  <option key={month} value={month}>
                    {formatMonth(month)}
                  </option>
                ))}
              </select>
            </div>
            <div className='form-field'>
              <label>סוג נתונים:</label>
              <div className='radio-group'>
                <div className='radio-option'>
                  <input
                    type='radio'
                    id='clear-companies'
                    name='clear-type'
                    value='companies'
                    checked={clearDataType === "companies"}
                    onChange={(e) => setClearDataType(e.target.value)}
                  />
                  <label htmlFor='clear-companies'>חברות</label>
                </div>
                <div className='radio-option'>
                  <input
                    type='radio'
                    id='clear-tm'
                    name='clear-type'
                    value='tm'
                    checked={clearDataType === "tm"}
                    onChange={(e) => setClearDataType(e.target.value)}
                  />
                  <label htmlFor='clear-tm'>T&M</label>
                </div>
                <div className='radio-option'>
                  <input
                    type='radio'
                    id='clear-all'
                    name='clear-type'
                    value='all'
                    checked={clearDataType === "all"}
                    onChange={(e) => setClearDataType(e.target.value)}
                  />
                  <label htmlFor='clear-all'>הכל</label>
                </div>
              </div>
            </div>
            <button
              type='button'
              className='clear-button'
              onClick={handleClearData}
              disabled={clearing || !clearMonth}
            >
              {clearing ? "מוחק..." : "🗑️ מחק נתונים"}
            </button>
          </div>
        </div>
      </div>

      {/* Upload Results Modal */}
      {showResultsModal && (
        <UploadResultsModal
          results={uploadResult}
          onClose={handleResultsClose}
        />
      )}

      {/* Employee Approval Modal */}
      {showApprovalModal && (
        <EmployeeApprovalModal
          newEmployees={newEmployees}
          onClose={handleApprovalModalClose}
          onApproved={handleEmployeeApproved}
        />
      )}

      {/* Employee Reference Data Modal - for employees copied from other partnerships */}
      {showReferenceModal && (
        <EmployeeReferenceModal
          employees={newEmployees}
          onClose={handleReferenceModalClose}
          onApproved={handleReferenceApproved}
        />
      )}
    </div>
  );
};

export default Upload;
